/**
 * OPERATR CounterFind Mailbox Pilot
 * AI-powered email classification, filing and attention management for CounterFind.
 * 
 * Access Control: Only counterfind@operatr.com can view this page.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, AlertTriangle, CheckCircle, Clock, BarChart3, Search,
  Filter, RefreshCw, Settings, ChevronRight, ChevronDown, X,
  Check, Edit2, Archive, User, MessageSquare, Tag, Calendar,
  ExternalLink, Copy, AlertCircle, Zap, TrendingUp, Users,
  FileText, Globe, Building, Shield, Eye, Loader2, Play,
  ChevronLeft, MoreVertical, Send, Inbox, FolderOpen, Star
} from 'lucide-react';

// Use window.location.origin for same-origin API calls
const API = typeof window !== 'undefined' ? window.location.origin : '';
const AUTHORIZED_EMAIL = 'counterfind@operatr.com';

// Simple XHR-based fetch that handles rrweb response corruption
const xhrFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    
    // Set headers
    if (options.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
    }
    if (options.body && !options.headers?.['Content-Type']) {
      xhr.setRequestHeader('Content-Type', 'application/json');
    }
    
    xhr.onload = function() {
      let data;
      try {
        // Handle rrweb corruption by cleaning response
        let responseText = xhr.responseText;
        if (responseText && typeof responseText === 'string') {
          // Find first valid JSON start
          const jsonStart = responseText.indexOf('{');
          if (jsonStart > 0) {
            responseText = responseText.substring(jsonStart);
          }
          // Find last valid JSON end
          const lastBrace = responseText.lastIndexOf('}');
          if (lastBrace !== -1 && lastBrace < responseText.length - 1) {
            responseText = responseText.substring(0, lastBrace + 1);
          }
        }
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('XHR parse error:', e, xhr.responseText?.substring(0, 100));
        reject(e);
        return;
      }
      resolve({ ok: xhr.status >= 200 && xhr.status < 300, status: xhr.status, json: () => Promise.resolve(data) });
    };
    
    xhr.onerror = function() {
      reject(new Error('XHR network error'));
    };
    
    xhr.send(options.body || null);
  });
};

// Classification categories with colors
const CATEGORIES = {
  'Report Received': { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Inbox },
  'Removal Successful': { color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  'Report Rejected / Information Required': { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: AlertTriangle },
  'Counter-Notice': { color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: Shield },
  'Retraction': { color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: RefreshCw },
  'Seller Inbound': { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', icon: MessageSquare }
};

// Priority colors
const PRIORITIES = {
  'Critical': 'bg-red-600 text-white',
  'High': 'bg-red-500/20 text-red-400 border-red-500/30',
  'Medium': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  'Low': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

// Status colors
const STATUSES = {
  'New': 'bg-blue-500/20 text-blue-400',
  'Pending Review': 'bg-yellow-500/20 text-yellow-400',
  'Approved': 'bg-green-500/20 text-green-400',
  'Filed': 'bg-green-600/20 text-green-300',
  'Needs Action': 'bg-orange-500/20 text-orange-400',
  'In Progress': 'bg-purple-500/20 text-purple-400',
  'Resolved': 'bg-gray-500/20 text-gray-400',
  'Classification Failed': 'bg-red-500/20 text-red-400'
};

const MailboxPage = () => {
  const navigate = useNavigate();
  const [userEmail, setUserEmail] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(true);
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  const [messages, setMessages] = useState([]);
  const [totalMessages, setTotalMessages] = useState(0);
  const [analytics, setAnalytics] = useState(null);
  
  // Filters
  const [selectedMailbox, setSelectedMailbox] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [showAttentionOnly, setShowAttentionOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  
  // Detail drawer
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [messageDetails, setMessageDetails] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // Modals
  const [showSettings, setShowSettings] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [walkthroughStep, setWalkthroughStep] = useState(0);
  
  // Actions
  const [actionLoading, setActionLoading] = useState(null);
  const [newNote, setNewNote] = useState('');
  
  // Mailboxes
  const mailboxes = [
    { value: 'all', label: 'All Mailboxes' },
    { value: 'enforcement@counterfind.com', label: 'enforcement@counterfind.com' },
    { value: 'amazon@counterfind.com', label: 'amazon@counterfind.com' },
    { value: 'marketplace@counterfind.com', label: 'marketplace@counterfind.com' },
    { value: 'sellers@counterfind.com', label: 'sellers@counterfind.com' }
  ];

  // Check authorization
  useEffect(() => {
    // Try auth_user first (new auth system), then fall back to user (legacy)
    const storedUser = localStorage.getItem('auth_user') || localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const email = user.email || '';
        setUserEmail(email);
        setIsAuthorized(email.toLowerCase() === AUTHORIZED_EMAIL);
      } catch {
        setIsAuthorized(false);
      }
    }
    setLoading(false);
  }, []);

  // Seed demo data on first load
  useEffect(() => {
    if (isAuthorized && userEmail) {
      seedDemoData();
    }
  }, [isAuthorized, userEmail]);

  // Fetch data when filters change
  useEffect(() => {
    if (isAuthorized && userEmail) {
      fetchDashboard();
      fetchMessages();
    }
  }, [isAuthorized, userEmail, selectedMailbox, selectedCategory, selectedStatus, selectedPriority, showAttentionOnly, searchQuery, page, activeTab]);

  const seedDemoData = async () => {
    try {
      await xhrFetch(`${API}/api/mailbox/demo/seed?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST'
      });
    } catch (err) {
      console.error('Error seeding demo data:', err);
    }
  };

  const fetchDashboard = async () => {
    try {
      const res = await xhrFetch(`${API}/api/mailbox/dashboard?user_email=${encodeURIComponent(userEmail)}&mailbox=${selectedMailbox}`);
      const data = await res.json();
      if (data.success) {
        setDashboard(data.data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  };

  const fetchMessages = async () => {
    try {
      let url = `${API}/api/mailbox/messages?user_email=${encodeURIComponent(userEmail)}&page=${page}&limit=${limit}&mailbox=${selectedMailbox}`;
      
      // Apply tab filters
      if (activeTab === 'attention') {
        url += '&attention=true';
      } else if (activeTab === 'qa') {
        url += '&status=Pending Review';
      } else if (activeTab !== 'all' && activeTab !== 'resolved') {
        url += `&category=${encodeURIComponent(activeTab)}`;
      } else if (activeTab === 'resolved') {
        url += '&status=Resolved';
      }
      
      if (selectedCategory) url += `&category=${encodeURIComponent(selectedCategory)}`;
      if (selectedStatus) url += `&status=${encodeURIComponent(selectedStatus)}`;
      if (selectedPriority) url += `&priority=${encodeURIComponent(selectedPriority)}`;
      if (showAttentionOnly) url += '&attention=true';
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const res = await xhrFetch(url);
      const data = await res.json();
      if (data.success) {
        setMessages(data.data);
        setTotalMessages(data.pagination.total);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  const fetchMessageDetails = async (messageId) => {
    setDetailLoading(true);
    try {
      const res = await xhrFetch(`${API}/api/mailbox/messages/${messageId}?user_email=${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      if (data.success) {
        setMessageDetails(data.data);
      }
    } catch (err) {
      console.error('Error fetching message details:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const openMessageDrawer = (message) => {
    setSelectedMessage(message);
    setDrawerOpen(true);
    fetchMessageDetails(message.id);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedMessage(null);
    setMessageDetails(null);
  };

  // Actions
  const handleApprove = async (messageId) => {
    setActionLoading('approve');
    try {
      const res = await xhrFetch(`${API}/api/mailbox/messages/${messageId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: userEmail })
      });
      const data = await res.json();
      if (data.success) {
        fetchMessages();
        fetchDashboard();
        if (messageDetails) fetchMessageDetails(messageId);
      }
    } catch (err) {
      console.error('Error approving:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCorrect = async (messageId, newCategory) => {
    setActionLoading('correct');
    try {
      const res = await xhrFetch(`${API}/api/mailbox/messages/${messageId}/correct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_email: userEmail, 
          new_category: newCategory,
          correction_reason: 'Manual correction'
        })
      });
      const data = await res.json();
      if (data.success) {
        fetchMessages();
        fetchDashboard();
        if (messageDetails) fetchMessageDetails(messageId);
      }
    } catch (err) {
      console.error('Error correcting:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleApplyLabel = async (messageId) => {
    setActionLoading('label');
    try {
      const res = await xhrFetch(`${API}/api/mailbox/messages/${messageId}/apply-label?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        fetchMessages();
        fetchDashboard();
        if (messageDetails) fetchMessageDetails(messageId);
      }
    } catch (err) {
      console.error('Error applying label:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResolve = async (messageId) => {
    setActionLoading('resolve');
    try {
      const res = await xhrFetch(`${API}/api/mailbox/messages/${messageId}/resolve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: userEmail })
      });
      const data = await res.json();
      if (data.success) {
        fetchMessages();
        fetchDashboard();
        if (messageDetails) fetchMessageDetails(messageId);
      }
    } catch (err) {
      console.error('Error resolving:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddNote = async (messageId) => {
    if (!newNote.trim()) return;
    setActionLoading('note');
    try {
      const res = await xhrFetch(`${API}/api/mailbox/messages/${messageId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_email: userEmail, body: newNote })
      });
      const data = await res.json();
      if (data.success) {
        setNewNote('');
        fetchMessageDetails(messageId);
      }
    } catch (err) {
      console.error('Error adding note:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetDemo = async () => {
    setLoading(true);
    try {
      await xhrFetch(`${API}/api/mailbox/demo/reset?user_email=${encodeURIComponent(userEmail)}`, {
        method: 'POST'
      });
      fetchDashboard();
      fetchMessages();
    } catch (err) {
      console.error('Error resetting demo:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setActionLoading('sync');
    try {
      // In demo mode, just refresh data
      await fetchDashboard();
      await fetchMessages();
    } catch (err) {
      console.error('Error syncing:', err);
    } finally {
      setActionLoading(null);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  // Tab counts
  const getTabCount = (tab) => {
    if (!dashboard) return 0;
    switch (tab) {
      case 'attention': return dashboard.requires_attention?.count || 0;
      case 'qa': return dashboard.qa_queue?.count || 0;
      default: return totalMessages;
    }
  };

  // Unauthorized view
  if (!loading && !isAuthorized) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Unauthorized Access</h1>
          <p className="text-gray-400 mb-4">This page is only available to counterfind@operatr.com</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Demo Mode Banner */}
      {isDemoMode && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/30 px-4 py-2">
          <div className="max-w-[1800px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-400" />
              <span className="text-sm text-yellow-400">
                <strong>Demo Mode</strong> — This pilot is currently using simulated mailbox data.
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleResetDemo}
                className="text-xs px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded hover:bg-yellow-500/30 transition-colors"
              >
                Reset Demo
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="text-xs px-3 py-1 bg-purple-500/20 text-purple-400 rounded hover:bg-purple-500/30 transition-colors"
              >
                Connect Google Workspace
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4" data-testid="mailbox-header">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-3" data-testid="mailbox-title">
                <Mail className="w-7 h-7 text-purple-400" />
                Mailbox Intelligence
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                AI-powered email classification, filing and attention management for CounterFind.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowWalkthrough(true)}
                data-testid="start-walkthrough-btn"
                className="flex items-center gap-2 px-3 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors text-sm"
              >
                <Play className="w-4 h-4" />
                Start Pilot Walkthrough
              </button>
              <button
                onClick={handleSync}
                disabled={actionLoading === 'sync'}
                data-testid="sync-mailbox-btn"
                className="flex items-center gap-2 px-3 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
              >
                {actionLoading === 'sync' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Sync Mailbox
              </button>
              <button
                onClick={() => setShowSettings(true)}
                data-testid="open-settings-btn"
                className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Filters Row */}
          <div className="flex items-center gap-4 flex-wrap">
            <select
              value={selectedMailbox}
              onChange={(e) => setSelectedMailbox(e.target.value)}
              data-testid="mailbox-filter-select"
              className="bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
            >
              {mailboxes.map(mb => (
                <option key={mb.value} value={mb.value}>{mb.label}</option>
              ))}
            </select>

            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search emails..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-emails-input"
                className="w-full bg-white/5 border border-white/20 rounded-lg pl-10 pr-4 py-2 text-sm focus:outline-none focus:border-purple-500"
              />
            </div>

            <div className="flex items-center gap-2 ml-auto">
              <span className={`text-xs px-2 py-1 rounded ${isDemoMode ? 'bg-yellow-500/20 text-yellow-400' : 'bg-green-500/20 text-green-400'}`}>
                {isDemoMode ? 'Demo Mode' : 'Live'}
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-6 py-6" data-testid="mailbox-main">
        {/* Summary Cards */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6" data-testid="summary-cards">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-blue-500/50 transition-colors" data-testid="card-new-emails">
              <div className="flex items-center justify-between mb-2">
                <Inbox className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-2xl font-bold">{dashboard.new_emails?.count || 0}</div>
              <div className="text-xs text-gray-400">{dashboard.new_emails?.label}</div>
            </div>

            <div 
              className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-red-500/50 transition-colors cursor-pointer"
              onClick={() => setActiveTab('attention')}
              data-testid="card-attention"
            >
              <div className="flex items-center justify-between mb-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div className="text-2xl font-bold text-red-400">{dashboard.requires_attention?.count || 0}</div>
              <div className="text-xs text-gray-400">{dashboard.requires_attention?.label}</div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-green-500/50 transition-colors" data-testid="card-classified">
              <div className="flex items-center justify-between mb-2">
                <Zap className="w-5 h-5 text-green-400" />
              </div>
              <div className="text-2xl font-bold text-green-400">{dashboard.auto_classified?.percentage || 0}%</div>
              <div className="text-xs text-gray-400">{dashboard.auto_classified?.label}</div>
            </div>

            <div 
              className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-yellow-500/50 transition-colors cursor-pointer"
              onClick={() => setActiveTab('qa')}
              data-testid="card-qa-queue"
            >
              <div className="flex items-center justify-between mb-2">
                <Eye className="w-5 h-5 text-yellow-400" />
              </div>
              <div className="text-2xl font-bold text-yellow-400">{dashboard.qa_queue?.count || 0}</div>
              <div className="text-xs text-gray-400">{dashboard.qa_queue?.label}</div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-purple-500/50 transition-colors" data-testid="card-confidence">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-purple-400" />
              </div>
              <div className="text-2xl font-bold">{dashboard.avg_confidence?.percentage || 0}%</div>
              <div className="text-xs text-gray-400">Average confidence</div>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-cyan-500/50 transition-colors" data-testid="card-processing-time">
              <div className="flex items-center justify-between mb-2">
                <Clock className="w-5 h-5 text-cyan-400" />
              </div>
              <div className="text-2xl font-bold">{dashboard.processing_time?.seconds || 0}s</div>
              <div className="text-xs text-gray-400">Avg. processing time</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-2 border-b border-white/10" data-testid="mailbox-tabs">
          {[
            { id: 'all', label: 'All Mail' },
            { id: 'attention', label: 'Requires Attention', count: dashboard?.requires_attention?.count },
            { id: 'qa', label: 'QA Queue', count: dashboard?.qa_queue?.count },
            { id: 'Report Received', label: 'Report Received' },
            { id: 'Removal Successful', label: 'Removal Successful' },
            { id: 'Report Rejected / Information Required', label: 'Info Required' },
            { id: 'Counter-Notice', label: 'Counter-Notices' },
            { id: 'Retraction', label: 'Retractions' },
            { id: 'Seller Inbound', label: 'Seller Inbound' },
            { id: 'resolved', label: 'Resolved' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setPage(1); }}
              data-testid={`tab-${tab.id.toLowerCase().replace(/ /g, '-').replace(/\//g, '-')}`}
              className={`px-3 py-2 text-sm whitespace-nowrap rounded-t-lg transition-colors ${
                activeTab === tab.id 
                  ? 'bg-white/10 text-white border-b-2 border-purple-500' 
                  : 'text-gray-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className={`ml-2 px-1.5 py-0.5 text-xs rounded ${
                  tab.id === 'attention' ? 'bg-red-500/20 text-red-400' :
                  tab.id === 'qa' ? 'bg-yellow-500/20 text-yellow-400' :
                  'bg-white/10'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Messages Table */}
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden" data-testid="messages-table-container">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="messages-table">
              <thead className="bg-white/5 border-b border-white/10">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Priority</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Received</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Sender</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Subject</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Marketplace</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Classification</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Confidence</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((msg) => {
                  const CategoryIcon = CATEGORIES[msg.primary_category]?.icon || Mail;
                  return (
                    <tr 
                      key={msg.id} 
                      onClick={() => openMessageDrawer(msg)}
                      data-testid={`message-row-${msg.id}`}
                      className={`border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${
                        msg.requires_attention ? 'bg-red-500/5' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        {msg.attention_priority && (
                          <span className={`text-xs px-2 py-1 rounded border ${PRIORITIES[msg.attention_priority] || PRIORITIES['Low']}`}>
                            {msg.attention_priority}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-1 rounded ${STATUSES[msg.operational_status] || STATUSES['New']}`}>
                          {msg.operational_status || 'New'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">{formatDate(msg.received_at)}</td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium truncate max-w-[150px]">{msg.sender_name || msg.sender_email}</div>
                        <div className="text-xs text-gray-500 truncate max-w-[150px]">{msg.sender_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm truncate max-w-[250px]">{msg.subject}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-400">{msg.marketplace || '-'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border ${CATEGORIES[msg.primary_category]?.color || 'bg-gray-500/20 text-gray-400'}`}>
                          <CategoryIcon className="w-3 h-3" />
                          {msg.primary_category?.replace(' / Information Required', '')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                msg.classification_confidence >= 0.95 ? 'bg-green-500' :
                                msg.classification_confidence >= 0.80 ? 'bg-yellow-500' :
                                'bg-red-500'
                              }`}
                              style={{ width: `${(msg.classification_confidence || 0) * 100}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-400">{Math.round((msg.classification_confidence || 0) * 100)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          {msg.operational_status === 'Pending Review' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleApprove(msg.id); }}
                              className="p-1.5 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition-colors"
                              title="Approve"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          )}
                          {msg.operational_status === 'Approved' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleApplyLabel(msg.id); }}
                              className="p-1.5 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 transition-colors"
                              title="Apply Label"
                            >
                              <Tag className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {messages.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                      <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No emails match the selected filters.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalMessages > limit && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10">
              <div className="text-sm text-gray-400">
                Showing {((page - 1) * limit) + 1} - {Math.min(page * limit, totalMessages)} of {totalMessages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 bg-white/10 rounded hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm">Page {page}</span>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page * limit >= totalMessages}
                  className="p-2 bg-white/10 rounded hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Detail Drawer */}
      {drawerOpen && selectedMessage && (
        <div className="fixed inset-0 z-50 flex" data-testid="message-drawer">
          <div className="flex-1 bg-black/50" onClick={closeDrawer} data-testid="drawer-backdrop" />
          <div className="w-full max-w-2xl bg-[#0a0a0a] border-l border-white/10 overflow-y-auto" data-testid="drawer-content">
            {detailLoading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : messageDetails ? (
              <div className="p-6">
                {/* Drawer Header */}
                <div className="flex items-start justify-between mb-6" data-testid="drawer-header">
                  <div className="flex-1">
                    <h2 className="text-lg font-bold mb-2" data-testid="drawer-subject">{messageDetails.message.subject}</h2>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-2 py-1 rounded border ${CATEGORIES[messageDetails.message.primary_category]?.color}`} data-testid="drawer-category">
                        {messageDetails.message.primary_category}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${STATUSES[messageDetails.message.operational_status]}`} data-testid="drawer-status">
                        {messageDetails.message.operational_status}
                      </span>
                      {messageDetails.message.requires_attention && (
                        <span className={`text-xs px-2 py-1 rounded border ${PRIORITIES[messageDetails.message.attention_priority]}`} data-testid="drawer-priority">
                          {messageDetails.message.attention_priority}
                        </span>
                      )}
                    </div>
                  </div>
                  <button onClick={closeDrawer} className="p-2 hover:bg-white/10 rounded-lg" data-testid="close-drawer-btn">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Email Header */}
                <div className="bg-white/5 rounded-lg p-4 mb-4" data-testid="drawer-email-header">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-gray-500 text-xs mb-1">From</div>
                      <div>{messageDetails.message.sender_name}</div>
                      <div className="text-gray-400 text-xs">{messageDetails.message.sender_email}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">To</div>
                      <div>{messageDetails.message.mailbox_email}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Received</div>
                      <div>{new Date(messageDetails.message.received_at).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs mb-1">Language</div>
                      <div>{messageDetails.message.detected_language || 'English'}</div>
                    </div>
                  </div>
                </div>

                {/* AI Summary */}
                {messageDetails.classification && (
                  <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4" data-testid="drawer-ai-summary">
                    <h3 className="text-sm font-semibold text-purple-400 mb-2 flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      AI Summary
                    </h3>
                    <p className="text-sm text-gray-300">{messageDetails.classification.summary}</p>
                    {messageDetails.classification.translated_summary && (
                      <p className="text-sm text-gray-400 mt-2 italic">
                        Translation: {messageDetails.classification.translated_summary}
                      </p>
                    )}
                  </div>
                )}

                {/* Classification Details */}
                {messageDetails.classification && (
                  <div className="bg-white/5 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-semibold mb-3">Classification Details</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Confidence</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                messageDetails.classification.confidence >= 0.95 ? 'bg-green-500' :
                                messageDetails.classification.confidence >= 0.80 ? 'bg-yellow-500' :
                                'bg-red-500'
                              }`}
                              style={{ width: `${messageDetails.classification.confidence * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium">{Math.round(messageDetails.classification.confidence * 100)}%</span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Marketplace</span>
                        <span className="text-sm">{messageDetails.classification.marketplace || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400 text-sm">Recommended Action</span>
                        <span className="text-sm text-right max-w-[60%]">{messageDetails.classification.recommended_action || '-'}</span>
                      </div>
                      {messageDetails.classification.deadline && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-sm">Deadline</span>
                          <span className="text-sm text-red-400">{messageDetails.classification.deadline}</span>
                        </div>
                      )}
                      {messageDetails.classification.case_id && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-400 text-sm">Case ID</span>
                          <span className="text-sm font-mono">{messageDetails.classification.case_id}</span>
                        </div>
                      )}
                      <div className="pt-2 border-t border-white/10">
                        <span className="text-gray-400 text-xs">Reasoning</span>
                        <p className="text-sm mt-1">{messageDetails.classification.reasoning_summary}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Attention Analysis */}
                {messageDetails.message.requires_attention && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-semibold text-red-400 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Requires Attention
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Priority</span>
                        <span className={`px-2 py-1 rounded text-xs ${PRIORITIES[messageDetails.message.attention_priority]}`}>
                          {messageDetails.message.attention_priority}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Reason</span>
                        <span>{messageDetails.message.attention_reason || messageDetails.classification?.attention_reason || '-'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Original Email */}
                <div className="bg-white/5 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-semibold mb-3">Original Email</h3>
                  <div className="text-sm text-gray-300 whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                    {messageDetails.message.sanitized_body_text || messageDetails.message.snippet}
                  </div>
                </div>

                {/* Internal Notes */}
                <div className="bg-white/5 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-semibold mb-3">Internal Notes</h3>
                  {messageDetails.notes?.length > 0 ? (
                    <div className="space-y-2 mb-3">
                      {messageDetails.notes.map((note, idx) => (
                        <div key={idx} className="bg-white/5 rounded p-2 text-sm">
                          <div className="text-xs text-gray-500 mb-1">{note.author_user_id} • {formatDate(note.created_at)}</div>
                          <div>{note.body}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mb-3">No notes yet.</p>
                  )}
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add a note..."
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="flex-1 bg-white/5 border border-white/20 rounded px-3 py-2 text-sm focus:outline-none focus:border-purple-500"
                      onKeyDown={(e) => e.key === 'Enter' && handleAddNote(messageDetails.message.id)}
                    />
                    <button
                      onClick={() => handleAddNote(messageDetails.message.id)}
                      disabled={!newNote.trim() || actionLoading === 'note'}
                      className="px-3 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50 transition-colors"
                    >
                      {actionLoading === 'note' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                    </button>
                  </div>
                </div>

                {/* Audit Timeline */}
                <div className="bg-white/5 rounded-lg p-4 mb-4">
                  <h3 className="text-sm font-semibold mb-3">Activity Timeline</h3>
                  {messageDetails.audit_events?.length > 0 ? (
                    <div className="space-y-2">
                      {messageDetails.audit_events.slice(0, 10).map((event, idx) => (
                        <div key={idx} className="flex items-start gap-3 text-sm">
                          <div className={`w-2 h-2 rounded-full mt-1.5 ${
                            event.event_type.includes('Approved') ? 'bg-green-500' :
                            event.event_type.includes('Failed') ? 'bg-red-500' :
                            event.event_type.includes('Corrected') ? 'bg-yellow-500' :
                            'bg-gray-500'
                          }`} />
                          <div className="flex-1">
                            <div className="text-gray-400">{event.event_type.replace(/([A-Z])/g, ' $1').trim()}</div>
                            <div className="text-xs text-gray-600">{event.actor_type}: {event.actor_id} • {formatDate(event.created_at)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No activity recorded.</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-white/10">
                  {messageDetails.message.operational_status === 'Pending Review' && (
                    <>
                      <button
                        onClick={() => handleApprove(messageDetails.message.id)}
                        disabled={actionLoading === 'approve'}
                        className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 transition-colors"
                      >
                        {actionLoading === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        Approve Classification
                      </button>
                      <select
                        onChange={(e) => e.target.value && handleCorrect(messageDetails.message.id, e.target.value)}
                        className="px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-sm focus:outline-none"
                        defaultValue=""
                      >
                        <option value="" disabled>Change Category...</option>
                        {Object.keys(CATEGORIES).map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </>
                  )}
                  {messageDetails.message.operational_status === 'Approved' && (
                    <button
                      onClick={() => handleApplyLabel(messageDetails.message.id)}
                      disabled={actionLoading === 'label'}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
                    >
                      {actionLoading === 'label' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />}
                      Apply Gmail Label
                    </button>
                  )}
                  {!['Resolved', 'Filed'].includes(messageDetails.message.operational_status) && (
                    <button
                      onClick={() => handleResolve(messageDetails.message.id)}
                      disabled={actionLoading === 'resolve'}
                      className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 disabled:opacity-50 transition-colors"
                    >
                      {actionLoading === 'resolve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                      Mark Resolved
                    </button>
                  )}
                </div>

                {/* Safety Notice */}
                <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                  <div className="flex items-start gap-2 text-xs text-yellow-400">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <strong>Safety Notice:</strong> Gmail labels are only applied after manual approval. 
                      This system does not send, reply to, or delete any emails.
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                Failed to load message details
              </div>
            )}
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-[#0a0a0a] border border-white/20 rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold">Mailbox Settings</h2>
              <button onClick={() => setShowSettings(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Mode */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Operating Mode</h3>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={isDemoMode}
                      onChange={() => setIsDemoMode(true)}
                      className="text-purple-500"
                    />
                    <span className="text-sm">Demo Mode</span>
                  </label>
                  <label className="flex items-center gap-2 opacity-50">
                    <input
                      type="radio"
                      checked={!isDemoMode}
                      disabled
                      className="text-purple-500"
                    />
                    <span className="text-sm">Live Mode (requires Google Workspace)</span>
                  </label>
                </div>
              </div>

              {/* Confidence Thresholds */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Confidence Thresholds</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-400">Auto-approve threshold</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      defaultValue="95"
                      className="w-full mt-1 bg-white/5 border border-white/20 rounded px-3 py-2 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Emails above this confidence can be auto-approved (disabled in pilot)</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">QA review threshold</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      defaultValue="80"
                      className="w-full mt-1 bg-white/5 border border-white/20 rounded px-3 py-2 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">Emails below this confidence require manual review</p>
                  </div>
                </div>
              </div>

              {/* Gmail Label Mapping */}
              <div>
                <h3 className="text-sm font-semibold mb-3">Gmail Label Mapping</h3>
                <div className="space-y-2">
                  {Object.entries({
                    'Report Received': 'CF/Report Received',
                    'Removal Successful': 'CF/Removal Successful',
                    'Report Rejected / Information Required': 'CF/Information Required',
                    'Counter-Notice': 'CF/Counter-Notice',
                    'Retraction': 'CF/Retraction',
                    'Seller Inbound': 'CF/Seller Inbound'
                  }).map(([cat, label]) => (
                    <div key={cat} className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 w-1/2 truncate">{cat}</span>
                      <input
                        type="text"
                        defaultValue={label}
                        className="flex-1 bg-white/5 border border-white/20 rounded px-2 py-1 text-xs"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Google Workspace Connection */}
              <div className="pt-4 border-t border-white/10">
                <h3 className="text-sm font-semibold mb-3">Google Workspace Connection</h3>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <p className="text-sm text-blue-300 mb-3">
                    To enable Live Mode, you need to configure Google Workspace with domain-wide delegation.
                  </p>
                  <ol className="text-xs text-gray-400 space-y-2 mb-4">
                    <li>1. Create a Google Cloud project with Gmail API enabled</li>
                    <li>2. Create a service account with domain-wide delegation</li>
                    <li>3. Grant the service account access to required mailboxes</li>
                    <li>4. Request only <code className="bg-white/10 px-1 rounded">gmail.modify</code> scope</li>
                  </ol>
                  <button
                    className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                    onClick={() => alert('Google Workspace configuration guide will be provided.')}
                  >
                    View Setup Guide
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-white/10">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pilot Walkthrough Modal */}
      {showWalkthrough && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80">
          <div className="bg-[#0a0a0a] border border-purple-500/30 rounded-xl w-full max-w-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Play className="w-5 h-5 text-purple-400" />
                Pilot Walkthrough
              </h2>
              <button onClick={() => setShowWalkthrough(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {[
                { step: 1, title: 'View Consolidated Mailbox Volume', desc: 'See the summary cards showing email volume, attention items, and classification stats.' },
                { step: 2, title: 'Open Requires Attention Tab', desc: 'Click the "Requires Attention" tab to see urgent emails needing review.' },
                { step: 3, title: 'Review a Rejected Report', desc: 'Find an "Information Required" email and open the detail drawer.' },
                { step: 4, title: 'View AI-Extracted Information', desc: 'See the case ID, listing IDs, and deadline extracted by AI.' },
                { step: 5, title: 'Correct a Classification', desc: 'Use the category dropdown to change a low-confidence classification.' },
                { step: 6, title: 'Approve a Classification', desc: 'Click "Approve Classification" on a high-confidence email.' },
                { step: 7, title: 'Apply Gmail Label', desc: 'After approval, click "Apply Gmail Label" to simulate labeling.' },
                { step: 8, title: 'View Audit Trail', desc: 'Check the Activity Timeline to see all actions recorded.' },
                { step: 9, title: 'Explore Analytics', desc: 'View category distribution and confidence metrics.' },
                { step: 10, title: 'Scale Demonstration', desc: 'Discuss expansion from 1 mailbox to 31-100 mailboxes.' }
              ].map(({ step, title, desc }) => (
                <div 
                  key={step}
                  className={`flex items-start gap-4 p-3 rounded-lg ${
                    walkthroughStep === step - 1 ? 'bg-purple-500/20 border border-purple-500/30' : 'bg-white/5'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    walkthroughStep >= step - 1 ? 'bg-purple-500 text-white' : 'bg-white/10 text-gray-500'
                  }`}>
                    {step}
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{title}</div>
                    <div className="text-xs text-gray-400 mt-1">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-between mt-6 pt-4 border-t border-white/10">
              <button
                onClick={() => setWalkthroughStep(s => Math.max(0, s - 1))}
                disabled={walkthroughStep === 0}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 disabled:opacity-50 transition-colors text-sm"
              >
                Previous
              </button>
              <button
                onClick={handleResetDemo}
                className="px-4 py-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors text-sm"
              >
                Reset Demo Data
              </button>
              <button
                onClick={() => {
                  if (walkthroughStep < 9) {
                    setWalkthroughStep(s => s + 1);
                  } else {
                    setShowWalkthrough(false);
                    setWalkthroughStep(0);
                  }
                }}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
              >
                {walkthroughStep < 9 ? 'Next Step' : 'Finish Walkthrough'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailboxPage;
